/** RÉGLAGES — prestations, horaires, rappels, page publique, plan. Onglets, pas de sidebar. */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BadgeEuro, CalendarOff, Copy, Download, ExternalLink, Lock, Plus, Save, Trash2,
} from "lucide-react";
import { api, messageErreur } from "../api";
import { useApp } from "../App";
import {
  AdresseAutocomplete, Bascule, Btn, Champ, Choix, Feuille, Micro, Pastille, Squelette,
} from "../components/Bits";
import { duree, euros, joursSemaine } from "../lib/format";
import { Galerie } from "../components/Photos";
import { THEMES } from "../lib/themes";

const MOYENS = [
  { c: "especes", l: "Espèces", champ: null },
  { c: "carte", l: "Carte bancaire (terminal)", champ: null },
  { c: "virement", l: "Virement", champ: "Ton IBAN" },
  { c: "paypal", l: "PayPal", champ: "paypal.me/tonpseudo" },
  { c: "revolut", l: "Revolut", champ: "revolut.me/tonpseudo" },
  { c: "lydia", l: "Lydia", champ: "Ton numéro ou ton lien Lydia" },
  { c: "cheque", l: "Chèque", champ: "À l'ordre de…" },
  { c: "stripe", l: "Paiement en ligne (Stripe)", champ: "Ton lien de paiement Stripe" },
];

/** Chaque ancre du hub « Plus » mène à un onglet précis, et si possible au bon bloc.
 *  Sans cette table, tous les liens retombaient sur « Prestations ». */
const ANCRES = {
  prestations: "prestations",
  horaires: "horaires",
  conges: "horaires",
  rappels: "rappels",
  sms: "rappels",
  page: "page",
  vitrine: "page",
  portfolio: "page",
  couleurs: "page",
  compte: "compte",
  profil: "compte",
  paiements: "compte",
  acomptes: "compte",
  abonnement: "compte",
  export: "compte",
};

const ONGLETS = [
  { c: "prestations", l: "Prestations" },
  { c: "horaires", l: "Horaires" },
  { c: "rappels", l: "Rappels" },
  { c: "page", l: "Page publique" },
  { c: "compte", l: "Compte" },
];

export default function Reglages() {
  const { compte, setCompte, rafraichir } = useApp();
  const emplacement = useLocation();
  const nav = useNavigate();
  const ancre = (emplacement.hash || "").replace("#", "");
  const [onglet, setOnglet] = useState(ANCRES[ancre] || "prestations");
  const [prestations, setPrestations] = useState(null);
  const [horaires, setHoraires] = useState(null);
  const [fermetures, setFermetures] = useState({});
  const [nouveauConge, setNouveauConge] = useState({ date: "", partie: "journee" });
  const [rappels, setRappels] = useState(compte?.rappels || {});
  const [profil, setProfil] = useState({
    activite: compte?.activite || "",
    telephone: compte?.telephone || "",
    adresse: compte?.adresse || "",
    instagram: compte?.instagram || "",
    tiktok: compte?.tiktok || "",
    snapchat: compte?.snapchat || "",
    bio: compte?.bio || "",
    slug: compte?.slug || "",
    delai_mini_heures: compte?.delai_mini_heures ?? 2,
    reservation_en_ligne: compte?.reservation_en_ligne !== false,
    moyens_paiement: compte?.moyens_paiement || [],
    theme: compte?.theme || "sauge",
    acompte: compte?.acompte || { actif: false, type: "fixe", valeur: 10 },
  });
  const [edition, setEdition] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [journal, setJournal] = useState(null);

  const charger = useCallback(async () => {
    try {
      const [p, h] = await Promise.all([api.get("/prestations"), api.get("/horaires")]);
      setPrestations(p.data);
      setHoraires(h.data.weekly || {});
      setFermetures(h.data.exceptions || {});
    } catch (e) {
      toast.error(messageErreur(e));
    }
  }, []);

  useEffect(() => {
    charger();
  }, [charger]);

  useEffect(() => {
    if (!ANCRES[ancre]) return;
    setOnglet(ANCRES[ancre]);
    const t = setTimeout(() => {
      document.getElementById(`bloc-${ancre}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
    return () => clearTimeout(t);
  }, [ancre]);

  useEffect(() => {
    if (onglet === "rappels")
      api.get("/rappels/journal").then(({ data }) => setJournal(data)).catch(() => {});
  }, [onglet]);

  /* ------------------------------------------------------------ prestations */
  const sauverPresta = async () => {
    const p = edition;
    if (!p.nom?.trim()) return toast.error("Donne un nom à ta prestation.");
    setEnvoi(true);
    try {
      const corps = {
        nom: p.nom.trim(),
        duree_min: Number(p.duree_min),
        buffer_min: Number(p.buffer_min || 0),
        prix: Number(p.prix),
        description: p.description || null,
        actif: p.actif !== false,
      };
      if (p.id) await api.patch(`/prestations/${p.id}`, corps);
      else await api.post("/prestations", corps);
      toast.success("Prestation enregistrée.");
      setEdition(null);
      charger();
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  const supprimerPresta = async (id) => {
    try {
      const { data } = await api.delete(`/prestations/${id}`);
      toast.success(
        data.archive
          ? "Prestation archivée : elle reste liée à tes anciens rendez-vous."
          : "Prestation supprimée."
      );
      setEdition(null);
      charger();
    } catch (e) {
      toast.error(messageErreur(e));
    }
  };

  /* --------------------------------------------------------------- horaires */
  const jourOuvert = (j) => Array.isArray(horaires?.[j]) && horaires[j].length > 0;
  const basculerJour = (j) =>
    setHoraires((h) => ({ ...h, [j]: jourOuvert(j) ? [] : [{ start: "09:00", end: "18:00" }] }));
  const PARTIES = { journee: [], matin: [{ start: "13:00", end: "19:00" }], apresmidi: [{ start: "09:00", end: "13:00" }] };

  const enregistrerFermetures = async (suivantes) => {
    setFermetures(suivantes);
    setEnvoi(true);
    try {
      const weekly = {};
      Object.entries(horaires || {}).forEach(([j, p]) => {
        if (p && p.length) weekly[String(j)] = p;
      });
      await api.put("/horaires", { weekly, exceptions: suivantes });
      rafraichir();
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  const ajouterConge = () => {
    if (!nouveauConge.date) return toast.error("Choisis une date à fermer.");
    if (fermetures[nouveauConge.date]) return toast.error("Cette journée est déjà dans ta liste.");
    enregistrerFermetures({ ...fermetures, [nouveauConge.date]: PARTIES[nouveauConge.partie] });
    setNouveauConge({ date: "", partie: "journee" });
    toast.success("Journée fermée. Plus aucune réservation possible dessus.");
  };

  const retirerConge = (cle) => {
    const suivantes = { ...fermetures };
    delete suivantes[cle];
    enregistrerFermetures(suivantes);
    toast.success("Journée rouverte.");
  };

  const majPlage = (j, champ, v) =>
    setHoraires((h) => ({ ...h, [j]: [{ ...(h[j]?.[0] || {}), [champ]: v }] }));

  const sauverHoraires = async () => {
    setEnvoi(true);
    try {
      const weekly = {};
      Object.entries(horaires).forEach(([j, p]) => {
        if (p && p.length) weekly[String(j)] = p;
      });
      await api.put("/horaires", { weekly, exceptions: fermetures });
      toast.success("Horaires enregistrés.");
      rafraichir();
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  /* ---------------------------------------------------------------- rappels */
  const sauverRappels = async (patch) => {
    const nouveaux = { ...rappels, ...patch };
    setRappels(nouveaux);
    try {
      await api.put("/rappels", {
        actif: nouveaux.actif !== false,
        j2_email: !!nouveaux.j2_email,
        j1_email: !!nouveaux.j1_email,
        matin_email: !!nouveaux.matin_email,
        demander_confirmation: nouveaux.demander_confirmation !== false,
        heure_matin: nouveaux.heure_matin || "08:00",
        ton: nouveaux.ton || "chaleureux",
      });
      setCompte({ ...compte, rappels: nouveaux });
    } catch (e) {
      toast.error(messageErreur(e));
    }
  };

  /* ----------------------------------------------------------------- profil */
  const sauverProfil = async () => {
    setEnvoi(true);
    try {
      const { data } = await api.patch("/profil", {
        activite: profil.activite,
        telephone: profil.telephone || null,
        adresse: profil.adresse || null,
        instagram: profil.instagram || null,
        tiktok: profil.tiktok || null,
        snapchat: profil.snapchat || null,
        bio: profil.bio || null,
        slug: profil.slug,
        delai_mini_heures: Number(profil.delai_mini_heures),
        reservation_en_ligne: profil.reservation_en_ligne,
        theme: profil.theme || "sauge",
        moyens_paiement: (profil.moyens_paiement || []).map((m) => ({
          code: m.code,
          actif: true,
          detail: (m.detail || "").trim() || null,
        })),
        acompte: {
          actif: !!profil.acompte?.actif,
          type: profil.acompte?.type || "fixe",
          valeur: Number(profil.acompte?.valeur) || 10,
        },
      });
      setCompte(data);
      setProfil((p) => ({ ...p, slug: data.slug }));
      toast.success("Modifications enregistrées.");
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  const abonner = async (formule) => {
    setEnvoi(true);
    try {
      const { data } = await api.post("/abonnement", { formule });
      setCompte(data);
      toast.success(
        formule === "annuel"
          ? "Abonnement annuel activé. Deux mois offerts."
          : "Abonnement mensuel activé."
      );
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  const resilier = async () => {
    setEnvoi(true);
    try {
      const { data } = await api.delete("/abonnement");
      setCompte(data);
      toast.success("Abonnement résilié. Tes données restent à toi.");
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setEnvoi(false);
    }
  };

  const exporter = async (format) => {
    try {
      const chemin = format === "csv" ? "/export/clientes.csv" : "/export";
      const { data } = await api.get(chemin, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = format === "csv" ? "servae-clientes.csv" : "servae-mes-donnees.json";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Export téléchargé. Il est à toi.");
    } catch (e) {
      toast.error(messageErreur(e, "Export impossible pour le moment."));
    }
  };

  const lienPublic = `${window.location.origin}/r/${compte?.slug}`;
  const canaux = compte?.canaux || {};

  return (
    <div className="pb-8">
      <div className="pb-4 border-b border-[var(--rule)]">
        <Micro>Réglages</Micro>
        <h1 className="display text-[26px] md:text-[32px] mt-1.5">{compte?.activite}</h1>
      </div>

      <div className="flex gap-1 py-4 overflow-x-auto no-scrollbar border-b border-[var(--rule)]">
        {ONGLETS.map((o) => (
          <button
            key={o.c}
            onClick={() => {
              setOnglet(o.c);
              nav(`/reglages#${o.c}`, { replace: true });
            }}
            data-testid={`reglages-${o.c}`}
            className={`h-10 px-3.5 rounded-[4px] text-[12px] font-semibold uppercase tracking-[0.1em] whitespace-nowrap ${
              onglet === o.c
                ? "bg-[var(--accent-tint)] text-[var(--ink)]"
                : "text-[var(--ink-muted)] hover:text-[var(--ink)]"
            }`}
          >
            {o.l}
          </button>
        ))}
      </div>

      <div className="pt-6 max-w-[720px]">
        {/* ============================================================ presta */}
        {onglet === "prestations" &&
          (prestations === null ? (
            <Squelette lignes={3} />
          ) : (
            <div>
              <div className="flex items-center justify-between pb-3">
                <p className="text-[13px] text-[var(--ink-soft)] max-w-[46ch]">
                  Le battement est le temps de remise en état. Il bloque l'agenda sans être
                  facturé — et il est compté dans ta rentabilité horaire.
                </p>
                <Btn
                  taille="sm"
                  icone={Plus}
                  onClick={() => setEdition({ nom: "", duree_min: 60, buffer_min: 10, prix: 50, actif: true })}
                  data-testid="btn-nouvelle-presta"
                >
                  Ajouter
                </Btn>
              </div>
              <div className="border-t border-[var(--rule)]">
                {prestations.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => setEdition(p)}
                    data-testid={`presta-${p.id}`}
                    className="w-full text-left flex items-center gap-3 py-3.5 border-b border-[var(--rule)] hover:bg-[rgba(23,19,16,0.02)]"
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block text-[15px] font-semibold truncate">
                        {p.nom} {!p.actif && <Pastille ton="neutre">Archivée</Pastille>}
                      </span>
                      <span className="block text-[13px] text-[var(--ink-soft)]">
                        {duree(p.duree_min)}
                        {p.buffer_min ? ` + ${p.buffer_min} min` : ""}
                      </span>
                    </span>
                    <span className="num text-[15px] shrink-0">{euros(p.prix)}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}

        {/* ========================================================== horaires */}
        {onglet === "horaires" &&
          (horaires === null ? (
            <Squelette lignes={3} />
          ) : (
            <div id="bloc-horaires">
              <p className="text-[13.5px] pb-4 max-w-[52ch]" style={{ color: "var(--text-muted)" }}>
                Tes horaires habituels. Servae ne proposera jamais un créneau en dehors, ni un
                créneau dont le battement dépasserait ta fermeture. Coupe un jour pour en faire
                ton jour de repos.
              </p>

              <div
                className="overflow-hidden"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-lg)",
                }}
              >
                {joursSemaine.map((nom, j) => {
                  const ouvert = jourOuvert(j);
                  return (
                    <div
                      key={j}
                      className="flex flex-wrap items-center gap-3 px-4 py-3"
                      style={{ borderBottom: j < 6 ? "1px solid var(--border)" : "none" }}
                    >
                      <button
                        onClick={() => basculerJour(j)}
                        data-testid={`reglages-jour-${j}`}
                        className="inline-flex items-center gap-2.5 w-[136px] shrink-0 text-left cible-tactile"
                        title={ouvert ? "Fermer ce jour" : "Ouvrir ce jour"}
                      >
                        <span
                          className="inline-flex items-center px-0.5 shrink-0"
                          style={{
                            height: 22,
                            width: 38,
                            borderRadius: 999,
                            background: ouvert ? "var(--accent)" : "var(--border-strong, var(--border))",
                            justifyContent: ouvert ? "flex-end" : "flex-start",
                            transition: "background-color .2s ease",
                          }}
                        >
                          <span style={{ height: 18, width: 18, borderRadius: 999, background: "#fff" }} />
                        </span>
                        <span
                          className="text-[15px] font-semibold"
                          style={{ color: ouvert ? "var(--text)" : "var(--text-subtle)" }}
                        >
                          {nom}
                        </span>
                      </button>
                      {ouvert ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="time"
                            value={horaires[j][0].start}
                            onChange={(e) => majPlage(j, "start", e.target.value)}
                            data-testid={`reglages-debut-${j}`}
                            className="h-10 px-2.5 num text-[15px] outline-none focus:border-[var(--accent)]"
                            style={{
                              background: "var(--bg)",
                              border: "1px solid var(--border)",
                              borderRadius: "var(--radius-sm)",
                            }}
                          />
                          <span style={{ color: "var(--text-subtle)" }}>→</span>
                          <input
                            type="time"
                            value={horaires[j][0].end}
                            onChange={(e) => majPlage(j, "end", e.target.value)}
                            data-testid={`reglages-fin-${j}`}
                            className="h-10 px-2.5 num text-[15px] outline-none focus:border-[var(--accent)]"
                            style={{
                              background: "var(--bg)",
                              border: "1px solid var(--border)",
                              borderRadius: "var(--radius-sm)",
                            }}
                          />
                        </div>
                      ) : (
                        <span className="text-[13.5px]" style={{ color: "var(--text-subtle)" }}>
                          Jour de repos
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              <Btn icone={Save} onClick={sauverHoraires} charge={envoi} className="mt-4" data-testid="btn-sauver-horaires">
                Enregistrer les horaires
              </Btn>

              {/* ------------------------------------------------------ congés */}
              <div id="bloc-conges" className="mt-10 pt-6" style={{ borderTop: "1px solid var(--border)" }}>
                <Micro className="mb-2 block">Congés et jours fermés</Micro>
                <p className="text-[13.5px] pb-4 max-w-[52ch]" style={{ color: "var(--text-muted)" }}>
                  Une date précise, en plus de tes horaires : vacances, formation, rendez-vous
                  médical. Personne ne pourra réserver dessus, et tes rendez-vous déjà pris ne
                  sont pas annulés — c'est toi qui décides quoi en faire.
                </p>

                <div className="flex flex-wrap items-end gap-2">
                  <input
                    type="date"
                    value={nouveauConge.date}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setNouveauConge({ ...nouveauConge, date: e.target.value })}
                    data-testid="conge-date"
                    className="h-11 px-3.5 num text-[15px] outline-none focus:border-[var(--accent)]"
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius-md)",
                    }}
                  />
                  <div
                    className="flex p-1 gap-1"
                    style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-full)" }}
                  >
                    {[
                      { c: "journee", l: "Toute la journée" },
                      { c: "matin", l: "Le matin" },
                      { c: "apresmidi", l: "L'après-midi" },
                    ].map((o) => (
                      <button
                        key={o.c}
                        onClick={() => setNouveauConge({ ...nouveauConge, partie: o.c })}
                        data-testid={`conge-partie-${o.c}`}
                        className="h-9 px-3.5 text-[12.5px] font-semibold"
                        style={{
                          borderRadius: "var(--radius-full)",
                          background: nouveauConge.partie === o.c ? "var(--surface)" : "transparent",
                          color: nouveauConge.partie === o.c ? "var(--text)" : "var(--text-subtle)",
                          boxShadow: nouveauConge.partie === o.c ? "var(--shadow-sm)" : "none",
                        }}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                  <Btn icone={CalendarOff} onClick={ajouterConge} charge={envoi} data-testid="btn-ajouter-conge">
                    Fermer cette date
                  </Btn>
                </div>

                <div className="mt-4 space-y-2 max-w-[520px]">
                  {Object.keys(fermetures).sort().map((cle) => {
                    const plages = fermetures[cle] || [];
                    return (
                      <div
                        key={cle}
                        data-testid={`conge-${cle}`}
                        className="flex items-center gap-3 px-4 py-3"
                        style={{
                          background: "var(--surface)",
                          border: "1px solid var(--border)",
                          borderRadius: "var(--radius-md)",
                        }}
                      >
                        <CalendarOff size={16} strokeWidth={1.8} style={{ color: "var(--accent)" }} />
                        <span className="text-[14.5px] font-semibold flex-1">
                          {new Date(`${cle}T12:00:00`).toLocaleDateString("fr-FR", {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                          })}
                        </span>
                        <span className="text-[13px]" style={{ color: "var(--text-subtle)" }}>
                          {plages.length
                            ? `ouvert ${plages.map((p) => `${p.start}–${p.end}`).join(", ")}`
                            : "fermé toute la journée"}
                        </span>
                        <button
                          onClick={() => retirerConge(cle)}
                          data-testid={`conge-retirer-${cle}`}
                          className="cible-tactile inline-flex items-center justify-center"
                          title="Rouvrir cette journée"
                          style={{ color: "var(--text-subtle)" }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    );
                  })}
                  {!Object.keys(fermetures).length && (
                    <p className="text-[13.5px]" style={{ color: "var(--text-subtle)" }}>
                      Aucune date fermée pour l'instant.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}

        {/* =========================================================== rappels */}
        {onglet === "rappels" && (
          <div>
            <p className="text-[13px] text-[var(--ink-soft)] pb-2 max-w-[48ch]">
              Le bouclier anti-lapin. Les rappels partent seuls, sans que tu y penses, avec un
              lien de confirmation que le client touche une fois.
            </p>

            <div className="surface rounded-[4px] p-4 my-4">
              <Micro>État du canal</Micro>
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[14px]">E-mail</span>
                  <Pastille ton={canaux.email?.actif ? "olive" : "ambre"}>
                    {canaux.email?.actif ? "Branché" : "À brancher"}
                  </Pastille>
                </div>
              </div>
              {!canaux.email?.actif && (
                <p className="text-[13px] text-[var(--ink-soft)] mt-3 pt-3 border-t border-[var(--rule)]">
                  Les rappels sont préparés et placés en file d'attente, mais rien ne part avant
                  que les identifiants d'envoi ne soient renseignés côté serveur. Nous préférons
                  te le dire clairement plutôt que de simuler un envoi.
                </p>
              )}
            </div>

            <div className="divide-y divide-[var(--rule)] border-y border-[var(--rule)]">
              <Bascule
                label="Rappels automatiques"
                description="Tout désactiver d'un coup, par exemple pendant tes congés"
                actif={rappels.actif !== false}
                surChangement={(v) => sauverRappels({ actif: v })}
                testid="bascule-rappels"
              />
              <Bascule
                label="E-mail deux jours avant"
                description="Le rappel qui laisse encore le temps de déplacer le rendez-vous"
                actif={!!rappels.j2_email}
                surChangement={(v) => sauverRappels({ j2_email: v })}
                testid="bascule-j2"
              />
              <Bascule
                label="E-mail la veille"
                description="Le plus efficace contre les oublis"
                actif={!!rappels.j1_email}
                surChangement={(v) => sauverRappels({ j1_email: v })}
                testid="bascule-j1"
              />
              <Bascule
                label="E-mail le matin même"
                description="À réserver aux clients à risque élevé : au-delà, cela devient pénible"
                actif={!!rappels.matin_email}
                surChangement={(v) => sauverRappels({ matin_email: v })}
                testid="bascule-matin"
              />
              <Bascule
                label="Demander une confirmation"
                description="Un lien à toucher. Une confirmation fait chuter le risque d'absence."
                actif={rappels.demander_confirmation !== false}
                surChangement={(v) => sauverRappels({ demander_confirmation: v })}
                testid="bascule-confirmation"
              />
            </div>

            {journal && (
              <div className="mt-6">
                <Micro className="pb-2 block border-b border-[var(--rule)]">
                  Journal d'envoi · {journal.en_attente} en attente
                </Micro>
                {journal.lignes.length === 0 ? (
                  <p className="text-[13px] text-[var(--ink-soft)] py-3">
                    Aucun rappel programmé pour l'instant. Ils se créent automatiquement à chaque
                    nouveau rendez-vous.
                  </p>
                ) : (
                  journal.lignes.slice(0, 12).map((l) => (
                    <div key={l.id} className="flex items-center gap-3 py-3 border-b border-[var(--rule)]">
                      <span className="flex-1 min-w-0">
                        <span className="block text-[14px] truncate">
                          {l.client || "—"} · E-mail{" "}
                          {l.etape === "j2" ? "(J-2)" : l.etape === "j1" ? "(veille)" : "(matin)"}
                        </span>
                        <span className="block text-[12px] text-[var(--ink-muted)] first-letter:uppercase">
                          {l.rdv_jour} · {l.rdv_heure}
                        </span>
                      </span>
                      <Pastille
                        ton={
                          l.status === "envoye"
                            ? "olive"
                            : l.status === "en_attente"
                            ? "neutre"
                            : "ambre"
                        }
                      >
                        {l.status === "envoye"
                          ? "Envoyé"
                          : l.status === "en_attente"
                          ? "En attente"
                          : l.status === "non_configure"
                          ? "Canal à brancher"
                          : "Échec"}
                      </Pastille>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* ============================================================== page */}
        {onglet === "page" && (
          <div className="space-y-4" id="bloc-page">
            <div
              className="p-4"
              id="bloc-vitrine"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-lg)",
              }}
            >
              <Micro>Ton lien de réservation</Micro>
              <div className="text-[14px] mt-2 break-all" data-testid="lien-public">{lienPublic}</div>
              <div className="flex gap-2 mt-3">
                <Btn
                  variante="fantome"
                  taille="sm"
                  icone={Copy}
                  onClick={() => {
                    navigator.clipboard
                      .writeText(lienPublic)
                      .then(() => toast.success("Lien copié. Colle-le dans ta bio."))
                      .catch(() => toast.error("Copie impossible."));
                  }}
                  data-testid="btn-copier-lien"
                >
                  Copier
                </Btn>
                <a href={lienPublic} target="_blank" rel="noreferrer">
                  <Btn variante="fantome" taille="sm" icone={ExternalLink} data-testid="btn-voir-page">
                    Voir la page
                  </Btn>
                </a>
              </div>
            </div>

            <div className="divide-y divide-[var(--rule)] border-y border-[var(--rule)]">
              <Bascule
                label="Réservation en ligne ouverte"
                description="Désactive-la si tu ne prends plus de nouvelles réservations"
                actif={profil.reservation_en_ligne}
                surChangement={(v) => setProfil({ ...profil, reservation_en_ligne: v })}
                testid="bascule-reservation"
              />
            </div>

            <Champ
              label="Identifiant du lien"
              value={profil.slug}
              onChange={(e) => setProfil({ ...profil, slug: e.target.value })}
              data-testid="champ-slug"
            />
            <Champ
              label="Délai minimum de réservation"
              type="number"
              value={profil.delai_mini_heures}
              onChange={(e) => setProfil({ ...profil, delai_mini_heures: e.target.value })}
              suffixe="h"
              indice="Empêche une réservation cinq minutes avant l'heure"
              data-testid="champ-delai"
            />
            <Champ
              label="Présentation courte"
              value={profil.bio}
              onChange={(e) => setProfil({ ...profil, bio: e.target.value })}
              placeholder="Nail art sur mesure, à Lyon 3e."
              data-testid="champ-bio"
            />
            <Btn icone={Save} onClick={sauverProfil} charge={envoi} data-testid="btn-sauver-page">
              Enregistrer
            </Btn>

            <div className="pt-6">
              <Micro className="pb-2.5 mb-3 block" style={{ borderBottom: "1px solid var(--border)" }}>
                Tes couleurs
              </Micro>
              <p className="text-[13px] mb-3" style={{ color: "var(--text-muted)" }}>
                Le thème habille ta page de réservation, l'espace de tes clientes et tes visuels
                de story. Ta page ne ressemble plus à celle de la voisine.
              </p>
              <div className="flex flex-wrap gap-2">
                {compte?.marque && (
                  <button
                    onClick={() => setProfil({ ...profil, theme: "perso" })}
                    data-testid="theme-perso"
                    className="flex items-center gap-2.5 h-11 pl-2 pr-4"
                    style={{
                      borderRadius: "var(--radius-full)",
                      background: "var(--surface)",
                      border: `1px solid ${
                        profil.theme === "perso" ? compte.marque.accent : "var(--border)"
                      }`,
                    }}
                  >
                    <span
                      style={{
                        height: 26,
                        width: 26,
                        borderRadius: "var(--radius-full)",
                        background: compte.marque.accent,
                        border: profil.theme === "perso" ? "2px solid var(--surface)" : "none",
                        boxShadow:
                          profil.theme === "perso" ? `0 0 0 2px ${compte.marque.accent}` : "none",
                      }}
                    />
                    <span
                      className="text-[13.5px] font-semibold"
                      style={{
                        color: profil.theme === "perso" ? compte.marque.accent : "var(--text-muted)",
                      }}
                    >
                      {compte.marque.nom}
                    </span>
                  </button>
                )}
                {Object.entries(THEMES).map(([code, t]) => {
                  const on = (profil.theme || "sauge") === code;
                  return (
                    <button
                      key={code}
                      onClick={() => setProfil({ ...profil, theme: code })}
                      data-testid={`theme-${code}`}
                      className="flex items-center gap-2.5 h-11 pl-2 pr-4"
                      style={{
                        borderRadius: "var(--radius-full)",
                        background: "var(--surface)",
                        border: `1px solid ${on ? t.accent : "var(--border)"}`,
                      }}
                    >
                      <span
                        style={{
                          height: 26,
                          width: 26,
                          borderRadius: "var(--radius-full)",
                          background: t.accent,
                          border: on ? "2px solid var(--surface)" : "none",
                          boxShadow: on ? `0 0 0 2px ${t.accent}` : "none",
                        }}
                      />
                      <span
                        className="text-[13.5px] font-semibold"
                        style={{ color: on ? t.accent : "var(--text-muted)" }}
                      >
                        {t.nom}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="text-[13px] mt-3" style={{ color: "var(--text-muted)" }}>
                Tu ne te retrouves dans aucune ?{" "}
                <Link
                  to="/marque"
                  className="font-semibold"
                  style={{ color: "var(--accent)" }}
                  data-testid="lien-brand-auto"
                >
                  Génère ta palette depuis tes photos
                </Link>
                .
              </p>
              <Btn
                icone={Save}
                className="mt-4"
                onClick={sauverProfil}
                charge={envoi}
                data-testid="btn-sauver-theme"
              >
                Appliquer mes couleurs
              </Btn>
            </div>

            <div className="pt-6" id="bloc-portfolio">
              <Galerie
                portfolio
                titre="Ton portfolio"
                aide="Tes plus belles réalisations, visibles en haut de ta page de réservation. C'est ça qui déclenche la prise de rendez-vous."
              />
            </div>

            <div
              className="flex items-start gap-2.5 p-4 mt-2"
              style={{ background: "var(--accent-soft)", borderRadius: "var(--radius-lg)" }}
            >
              <Lock size={16} strokeWidth={1.9} style={{ color: "var(--accent)", marginTop: 2, flexShrink: 0 }} />
              <p className="text-[13.5px]">
                Ton adresse exacte n'apparaît jamais sur ta page publique : tes clientes voient
                seulement ta ville. Ils reçoivent l'adresse complète après confirmation, dans
                leur espace personnel.
              </p>
            </div>
          </div>
        )}

        {/* ============================================================ compte */}
        {onglet === "compte" && (
          <div className="space-y-4">
            <div id="bloc-paiements" className="pb-5" style={{ borderBottom: "1px solid var(--border)" }}>
              <Micro className="pb-2.5 block">Moyens de paiement acceptés</Micro>
              <p className="text-[13px] mb-3" style={{ color: "var(--text-muted)" }}>
                Tu encaisses comme tu veux : Servae ne prend jamais de commission. Ce que tu
                coches apparaît sur ta page de réservation. Le détail (lien, IBAN, pseudo) n'est
                donné qu'à une cliente qui a déjà un rendez-vous, dans son espace personnel.
              </p>
              <div className="space-y-2">
                {MOYENS.map((m) => {
                  const actuel = (profil.moyens_paiement || []).find((x) => x.code === m.c);
                  return (
                    <div
                      key={m.c}
                      className="p-3.5"
                      style={{
                        background: "var(--surface)",
                        border: `1px solid ${actuel ? "var(--accent)" : "var(--border)"}`,
                        borderRadius: "var(--radius-md)",
                      }}
                    >
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!actuel}
                          data-testid={`paiement-${m.c}`}
                          onChange={(e) => {
                            const liste = (profil.moyens_paiement || []).filter(
                              (x) => x.code !== m.c
                            );
                            if (e.target.checked)
                              liste.push({ code: m.c, actif: true, detail: "" });
                            setProfil({ ...profil, moyens_paiement: liste });
                          }}
                          style={{ accentColor: "var(--accent)", height: 18, width: 18 }}
                        />
                        <span className="text-[15px] font-semibold">{m.l}</span>
                      </label>
                      {actuel && m.champ && (
                        <input
                          value={actuel.detail || ""}
                          placeholder={m.champ}
                          data-testid={`paiement-detail-${m.c}`}
                          onChange={(e) =>
                            setProfil({
                              ...profil,
                              moyens_paiement: (profil.moyens_paiement || []).map((x) =>
                                x.code === m.c ? { ...x, detail: e.target.value } : x
                              ),
                            })
                          }
                          className="w-full h-10 px-3 mt-3 text-[14px] outline-none focus:border-[var(--accent)]"
                          style={{
                            background: "var(--bg)",
                            border: "1px solid var(--border)",
                            borderRadius: "var(--radius-sm)",
                          }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <Btn
                icone={Save}
                className="mt-4"
                onClick={sauverProfil}
                charge={envoi}
                data-testid="btn-sauver-paiements"
              >
                Enregistrer mes moyens de paiement
              </Btn>
            </div>

            <div id="bloc-acomptes" className="pb-5" style={{ borderBottom: "1px solid var(--border)" }}>
              <Micro className="pb-2.5 block">Acompte à la réservation</Micro>
              <p className="text-[13px] mb-3" style={{ color: "var(--text-muted)" }}>
                Demandé aux nouvelles clientes pour bloquer le créneau. Le montant s'affiche sur
                ta page de réservation et dans l'espace de ta cliente, avec le moyen de paiement
                que tu as coché. Servae ne touche jamais à cet argent.
              </p>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!profil.acompte?.actif}
                  data-testid="bascule-acompte"
                  onChange={(e) =>
                    setProfil({
                      ...profil,
                      acompte: { ...(profil.acompte || { type: "fixe", valeur: 10 }), actif: e.target.checked },
                    })
                  }
                  style={{ accentColor: "var(--accent)", height: 18, width: 18 }}
                />
                <span className="text-[15px] font-semibold">Demander un acompte</span>
              </label>
              {profil.acompte?.actif && (
                <div className="flex flex-wrap items-end gap-2 mt-3">
                  <input
                    type="number"
                    min={1}
                    max={500}
                    value={profil.acompte?.valeur ?? 10}
                    onChange={(e) =>
                      setProfil({
                        ...profil,
                        acompte: { ...profil.acompte, valeur: Number(e.target.value) },
                      })
                    }
                    data-testid="champ-acompte-valeur"
                    className="h-11 w-24 px-3 num text-[15px] outline-none focus:border-[var(--accent)]"
                    style={{
                      background: "var(--surface)",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius-md)",
                    }}
                  />
                  <div
                    className="flex p-1 gap-1"
                    style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-full)" }}
                  >
                    {[
                      { c: "fixe", l: "En euros" },
                      { c: "pourcent", l: "En % du prix" },
                    ].map((o) => (
                      <button
                        key={o.c}
                        onClick={() => setProfil({ ...profil, acompte: { ...profil.acompte, type: o.c } })}
                        data-testid={`acompte-type-${o.c}`}
                        className="h-9 px-3.5 text-[12.5px] font-semibold"
                        style={{
                          borderRadius: "var(--radius-full)",
                          background: (profil.acompte?.type || "fixe") === o.c ? "var(--surface)" : "transparent",
                          color: (profil.acompte?.type || "fixe") === o.c ? "var(--text)" : "var(--text-subtle)",
                          boxShadow: (profil.acompte?.type || "fixe") === o.c ? "var(--shadow-sm)" : "none",
                        }}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <Btn icone={Save} className="mt-4" onClick={sauverProfil} charge={envoi} data-testid="btn-sauver-acompte">
                Enregistrer l'acompte
              </Btn>
            </div>

            <div id="bloc-profil" className="pt-2">
              <Micro className="pb-1 block">Mon profil</Micro>
            </div>
            <Champ
              label="Nom de l'activité"
              value={profil.activite}
              onChange={(e) => setProfil({ ...profil, activite: e.target.value })}
              data-testid="champ-activite"
            />
            <Champ
              label="Ton mobile"
              value={profil.telephone}
              onChange={(e) => setProfil({ ...profil, telephone: e.target.value })}
              inputMode="tel"
              data-testid="champ-telephone"
            />
            <AdresseAutocomplete
              label="Adresse"
              value={profil.adresse}
              onChange={(v) => setProfil({ ...profil, adresse: v })}
              indice="Ajoutée dans les rappels envoyés à tes clientes"
              testid="champ-adresse"
            />
            <Champ
              label="Instagram"
              value={profil.instagram}
              onChange={(e) => setProfil({ ...profil, instagram: e.target.value })}
              placeholder="tonstudio"
              data-testid="champ-instagram"
            />
            <Champ
              label="TikTok"
              value={profil.tiktok}
              onChange={(e) => setProfil({ ...profil, tiktok: e.target.value })}
              placeholder="tonstudio"
              data-testid="champ-tiktok"
            />
            <Champ
              label="Snapchat"
              value={profil.snapchat}
              onChange={(e) => setProfil({ ...profil, snapchat: e.target.value })}
              placeholder="tonstudio"
              data-testid="champ-snapchat"
            />
            <Btn icone={Save} onClick={sauverProfil} charge={envoi} data-testid="btn-sauver-compte">
              Enregistrer
            </Btn>

            <div id="bloc-abonnement" className="pt-6">
              <Micro className="pb-2.5 block">Ton abonnement</Micro>
              <div
                className="p-4"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-lg)",
                }}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[16px] font-semibold" data-testid="etat-abonnement">
                    {compte?.plan?.abonne
                      ? `Servae ${compte.plan.formule === "annuel" ? "à l'année" : "au mois"}`
                      : compte?.plan?.code === "essai"
                        ? "Essai en cours"
                        : "Essai terminé"}
                  </span>
                  <span className="num text-[20px]">
                    {compte?.plan?.formule === "annuel" ? "199 €/an" : "19,90 €/mois"}
                  </span>
                </div>
                <p className="text-[13.5px] mt-2" style={{ color: "var(--text-muted)" }}>
                  {compte?.plan?.abonne
                    ? `Prochain renouvellement le ${
                        compte.plan.renouvellement
                          ? new Date(compte.plan.renouvellement).toLocaleDateString("fr-FR")
                          : "—"
                      }. Tu peux résilier à tout moment, sans justification.`
                    : compte?.plan?.code === "essai"
                      ? `Tu as accès à tout jusqu'au ${
                          compte?.plan?.essai_fin
                            ? new Date(compte.plan.essai_fin).toLocaleDateString("fr-FR")
                            : "—"
                        }. Aucune fonction n'est bridée, aucune carte demandée.`
                      : "Ton essai est terminé. Tout reste accessible : choisis ta formule quand tu veux."}
                </p>

                {compte?.plan?.abonne ? (
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Btn
                      variante="fantome"
                      onClick={() => abonner(compte.plan.formule === "annuel" ? "mensuel" : "annuel")}
                      charge={envoi}
                      data-testid="btn-changer-formule"
                    >
                      Passer {compte.plan.formule === "annuel" ? "au mois" : "à l'année"}
                    </Btn>
                    <Btn variante="nu" onClick={resilier} charge={envoi} data-testid="btn-resilier">
                      Résilier
                    </Btn>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Btn icone={BadgeEuro} onClick={() => abonner("mensuel")} charge={envoi} data-testid="btn-abonner-mensuel">
                      19,90 € par mois
                    </Btn>
                    <Btn
                      variante="fantome"
                      onClick={() => abonner("annuel")}
                      charge={envoi}
                      data-testid="btn-abonner-annuel"
                    >
                      199 € par an — 2 mois offerts
                    </Btn>
                  </div>
                )}
                <p className="text-[12.5px] mt-3" style={{ color: "var(--text-subtle)" }}>
                  Le prélèvement automatique arrive à l'ouverture publique : pour l'instant, ton
                  abonnement est enregistré sans carte bancaire.
                </p>
              </div>
            </div>

            <div id="bloc-export" className="pt-6">
              <Micro className="pb-2.5 block">Exporter mes données</Micro>
              <p className="text-[13.5px] mb-3 max-w-[52ch]" style={{ color: "var(--text-muted)" }}>
                Tes clientes, tes rendez-vous, tes prestations, ton historique : tout part avec
                toi, quand tu veux. C'est ce qui distingue Servae d'un annuaire.
              </p>
              <div className="flex flex-wrap gap-2">
                <Btn variante="fantome" icone={Download} onClick={() => exporter("csv")} data-testid="btn-export-csv">
                  Mes clientes (CSV)
                </Btn>
                <Btn variante="nu" icone={Download} onClick={() => exporter("json")} data-testid="btn-export-json">
                  Tout mon compte (JSON)
                </Btn>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ------------------------------------------------- édition prestation */}
      <Feuille
        ouvert={!!edition}
        surFermeture={() => setEdition(null)}
        micro="Prestation"
        titre={edition?.id ? edition.nom : "Nouvelle prestation"}
        testid="feuille-presta"
        pied={
          <>
            {edition?.id && (
              <Btn variante="danger" icone={Trash2} onClick={() => supprimerPresta(edition.id)} data-testid="btn-supprimer-presta">
                Supprimer
              </Btn>
            )}
            <Btn onClick={sauverPresta} charge={envoi} data-testid="btn-sauver-presta">
              Enregistrer
            </Btn>
          </>
        }
      >
        {edition && (
          <div className="space-y-4">
            <Champ
              label="Nom"
              value={edition.nom}
              onChange={(e) => setEdition({ ...edition, nom: e.target.value })}
              data-testid="champ-presta-nom"
            />
            <div className="grid grid-cols-3 gap-3">
              <Champ
                label="Durée"
                type="number"
                value={edition.duree_min}
                onChange={(e) => setEdition({ ...edition, duree_min: e.target.value })}
                suffixe="min"
                data-testid="champ-presta-duree"
              />
              <Champ
                label="Battement"
                type="number"
                value={edition.buffer_min}
                onChange={(e) => setEdition({ ...edition, buffer_min: e.target.value })}
                suffixe="min"
                data-testid="champ-presta-buffer"
              />
              <Champ
                label="Prix"
                type="number"
                value={edition.prix}
                onChange={(e) => setEdition({ ...edition, prix: e.target.value })}
                suffixe="€"
                data-testid="champ-presta-prix"
              />
            </div>
            <Champ
              label="Description (visible sur ta page publique)"
              value={edition.description || ""}
              onChange={(e) => setEdition({ ...edition, description: e.target.value })}
              data-testid="champ-presta-desc"
            />
            <div className="border-y border-[var(--rule)]">
              <Bascule
                label="Proposée à la réservation"
                description="Désactivez pour la retirer sans perdre l'historique"
                actif={edition.actif !== false}
                surChangement={(v) => setEdition({ ...edition, actif: v })}
                testid="bascule-presta-actif"
              />
            </div>
          </div>
        )}
      </Feuille>
    </div>
  );
}
